/*
 * Title: Header/index.tsx
 * Author: Chris Rosa
 * Email: chris@encore.fans
 * Description: Desktop main header navigation
 * Date: 30 Jun 2022
 * Copyright (c) Encore, 2022
 * Version:0.1.0
 */

import React, { Fragment } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Popover, Transition } from "@headlessui/react";
import Buttonlink from "@components/Button";
import Logo from "@components/Logo";
import { useDispatch, useSelector } from "react-redux";
import FullName from "@components/FullName";
import common from "@common/common";
import mail from "@services/mail-service";
import Management from "@services/management-service";
import { isOrlando } from "@helpers/common";

interface Props { }

const Header: React.FC<Props | any> = (props) => {
  const management = Management.get();
  const { actions } = require("@redux/UserRedux");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fanId = useSelector((state: any) => (state.user?.user?.user?.id));
  const type =management?.program?.config?.type || "loyalty";
  const cname = management?.creator?.username;
  const pname = management?.program?.name;

  const handleLogout = () => {
    dispatch(actions.setLogout(true));
    navigate(`/${cname}/${pname}/logout`);
  };

  const primary = "var(--tile-inlay-primary)";
  const reverse = "var(--body-inlay-primary)";

  const contactUs = () => {
    window.location.href = `mailto:${process.env.REACT_APP_CONTACT_US_MAIL}`;
  }

  return (
    <div className={`sticky -top-1 bg-tile z-50 w-full hidden lg:block`}>

      {
        type == "etw" ? <div className="bg-body items-center pr-10 flex justify-end h-[90px]">
          <div>
            <Popover.Group className="flex items-center justify-between gap-x-10">
              <Popover className="relative">
                {({ open }) => (
                  <>
                    {
                      open ? <Popover.Button
                        className={
                          "flex items-center outline-none hover:opacity-80"
                        }>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-h-6 body-inlay-primary">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Popover.Button> :
                        <Popover.Button
                          className={
                            "flex items-center outline-none hover:opacity-80"
                          }>
                          <svg className="h-6 w-6 body-inlay-primary" width="24" height="24" viewBox="0 0 24 24" strokeWidth="1" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">  <path stroke="none" d="M0 0h24v24H0z" />  <line x1="4" y1="6" x2="20" y2="6" />  <line x1="4" y1="12" x2="20" y2="12" />  <line x1="4" y1="18" x2="20" y2="18" /></svg>

                        </Popover.Button>
                    }

                    <Transition
                      show={open}
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1">
                      <Popover.Panel className="absolute right-0 z-50 mt-3 transform">
                        <div className="relative bg-body shadow-[0px_6px_40px_0px_#0000001F] p-[40px] rounded-lg w-[480px] min-h-[440px]">
                          <div className="p-[25px_10px] bg-tile rounded-xl h-[128px] flex flex-col gap-3 justify-center items-center">
                            <p className="font-semibold text-[24px] leading-7 tile-inlay-primary">
                              <FullName/>
                            </p>
                            <Buttonlink
                              onClick={handleLogout}
                              className="cursor-pointer p-[10px_18px] btn-inlay-primary bg-btn w-max text-center rounded-full"
                              text="log out"
                            />
                          </div>
                          <div className="flex flex-col mt-10 gap-[30px] justify-center items-center">
                            {isOrlando() && 
                              <div>
                                  <a
                                      target="_blank"
                                      href={common.orlando_rules_url}
                                      className="block body-inlay-primary font-normal leading-6 text-center text-[20px] cursor-pointer relative" rel="noreferrer">
                                      Rules
                                  </a>
                              </div>
                            }
                            <div>
                              <a
                                target="_blank"
                                href={common.term_of_service_url}
                                className="block body-inlay-primary font-normal leading-6 text-center text-[20px] cursor-pointer relative" rel="noreferrer">
                                Terms of Use
                              </a>
                            </div>
                            <div>
                              <Buttonlink
                                onClick={contactUs}
                                className="block body-inlay-primary font-normal leading-6 text-center text-[20px] cursor-pointer relative"
                                text="Contact Encore"
                              />
                            </div>
                            <div>
                              <a
                               onClick={()=>mail.summary(fanId)}
                                className="block body-inlay-primary font-normal leading-6 text-center text-[20px] cursor-pointer relative">
                                Request Data Summary
                              </a>
                            </div>
                            <div>
                              <a
                                onClick={()=>mail.removal(fanId)}
                                className="block body-inlay-primary font-normal leading-6 text-center text-[20px] cursor-pointer relative">
                                Request Data Removal
                              </a>
                            </div>
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
            </Popover.Group>
          </div>
        </div> :
          <div className="container mx-auto">
            <div className="flex justify-between items-center h-[100px] lg:space-x-10">
              <div className="flex items-center justify-start">
                <NavLink to={`/${cname}/${pname}/earn`}>
                  <Logo
                    logo_letter="true"
                    className="max-h-[80px] h-full xl:w-[auto] w-[auto] object-contain rounded-full"
                  />
                </NavLink>
                <nav className="flex items-center justify-between w-full max-w-lg ml-10 space-x-5">
                  <NavLink
                    style={({ isActive }) => ({
                      color: isActive ? primary : reverse,
                    })}
                    to={`/${cname}/${pname}/earn`}
                    className={`text-[18px] hover:primary font-normal text-nowrap flex items-center justify-center py-2.5 px-2 relative outline-none`}>
                    My Rewards
                  </NavLink>
                  <NavLink
                    to={`/${cname}/${pname}/leaderboard`}
                    style={({ isActive }) => ({
                      color: isActive ? primary : reverse,
                    })}
                    className="text-[18px] hover:primary font-normal text-nowrap flex items-center justify-center py-2.5 px-2 relative outline-none">
                    Leaderboard
                  </NavLink>

                  {/* <NavLink
                    to="/invite-friend"
                    style={({ isActive }) => ({
                      color: isActive ? primary : "#fff",
                    })}
                    className="text-[18px] font-normal hover:primary text-nowrap flex items-center justify-center py-2.5 px-2 relative outline-none">
                    Invite a Friend
                  </NavLink>
                  <NavLink
                    to="/faqs"
                    style={({ isActive }) => ({
                      color: isActive ? primary : "#fff",
                    })}
                    className="text-[18px] font-normal hover:primary text-nowrap flex items-center justify-center py-2.5 px-2 relative outline-none">
                    FAQ
                  </NavLink>*/}
                </nav>
              </div>
              <div>
                <Popover.Group className="flex items-center justify-between gap-x-10">
                  <Popover className="relative">
                    <Popover.Button
                      className={
                        "flex items-center outline-none hover:opacity-80"
                      }>
                      <svg className="h-10 w-10 body-inlay-primary" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">  <path stroke="none" d="M0 0h24v24H0z" />  <line x1="4" y1="6" x2="20" y2="6" />  <line x1="4" y1="12" x2="20" y2="12" />  <line x1="4" y1="18" x2="20" y2="18" /></svg>

                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1">
                      <Popover.Panel className="absolute right-0 z-50 mt-3 transform w-72">
                        <div className="relative bg-tile px-3 py-4 rounded-lg shadow-black">
                          <p className="tile-inlay-primary font-normal text-[16px] text-center uppercase my-3">
                            Account
                          </p>
                          <Popover.Button as="div">
                            <NavLink
                              to={`/${cname}/${pname}/settings`}
                              className="py-3 hover:bg-[#000]/20 tile-inlay-primary font-light block text-center text-[16px] cursor-pointer relative">
                              Settings
                            </NavLink>
                          </Popover.Button>
                          <div className="h-[1px] w-full bg-tile" />
                          <Popover.Button as="div">
                            <a
                              target="_blank"
                              href={common.privacy_policy_url}
                              className="py-3 hover:bg-[#000]/20 tile-inlay-primary font-light block text-center text-[16px] cursor-pointer relative" rel="noreferrer">
                              Privacy Policy
                            </a>
                          </Popover.Button>
                          <div className="h-[1px] w-full bg-tile" />
                          <Popover.Button as="div">
                            <a
                              target="_blank"
                              href={common.term_of_service_url}
                              className="py-3 hover:bg-[#000]/20 tile-inlay-primary font-light block text-center text-[16px] cursor-pointer relative" rel="noreferrer">
                              Terms & Conditions
                            </a>
                          </Popover.Button>
                          {/* <div className="h-[1px] w-full bg-[#fff]/20" />
                          <Popover.Button as="div">
                            <NavLink
                              to="/signed-out/earn"
                              className="py-3 hover:bg-[#000]/20 text-white font-light block text-center text-[16px] cursor-pointer relative"
                            >
                              Signed Out View
                            </NavLink>
                          </Popover.Button> */}
                          <div className="h-[1px] w-full bg-tile" />
                          <Popover.Button as="div">
                            <Buttonlink
                              onClick={handleLogout}
                              className="cursor-pointer hover:bg-[#000]/20 text-[16px] text-red-500 tile-inlay-primary text-center py-3 block font-light tracking-wider w-full"
                              text="Log Out"
                            />
                          </Popover.Button>
                          {/* <p className="text-[white]/60 font-normal text-[10px] tracking-wider text-center mt-2">
                            Version: {process.env.REACT_APP_VERSION}
                          </p> */}
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </Popover>
                </Popover.Group>
              </div>
            </div>
          </div>
      }
    </div>
  );
}

export default Header;
